// CSV upload: one line = one scan. Reads the file, checks each line, and
// plans each scan with the same code as the search page (planScan).
// Columns: website | business_name | location | radius | extra_postcodes |
//   keywords | samples | tags | monitor
// Lists inside a cell are separated by ";". monitor is kept for later and ignored.

import { domainOf } from "./domains";
import { SAMPLE_COUNTS, buildScanRequest } from "./scanPlan";
import { planScan } from "./planScan";
import { RADIUS_MILES, outcodeOf } from "../locations/radius";

export const CSV_COLUMNS = [
  "website", "business_name", "location", "radius", "extra_postcodes", "keywords", "samples", "tags", "monitor",
];
const REQUIRED_COLUMNS = ["website", "location", "keywords"];
export const MAX_CSV_LINES = 50;
// Blank samples = the search page's defaults.
export const DEFAULT_SAMPLES = 3;
export const DEFAULT_SPACING = "hour";
const MAX_KEYWORDS = 20;
const MAX_KEYWORD_LENGTH = 100;
const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 50;

// The spacing part of a samples code ("3x15min" -> "15min"), as written in the template.
const SPACING_CODES = { minute: "1min", "15min": "15min", hour: "1hour", day: "1day" };
// Other ways of writing them that are accepted too.
const SPACING_ALIASES = {
  minute: ["1min", "min", "1mins", "mins", "1m", "1minute", "minute"],
  "15min": ["15min", "15mins", "15m", "15minutes"],
  hour: ["1hour", "hour", "1hours", "hours", "1h", "h", "1hr", "hr"],
  day: ["1day", "day", "1days", "days", "1d", "d"],
};

const FULL_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/;
const OUTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/;

export const TEMPLATE_EXAMPLE = [
  "acme-plumbing.co.uk", "Acme Plumbing", "S1 2AB;S10", "10", "S35",
  "emergency plumber;boiler repair", "3x1hour", "Prospect;Dream 100", "",
];

// The template file: the column names and one filled example line.
export function templateCsv() {
  return [CSV_COLUMNS, TEMPLATE_EXAMPLE].map(row => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

function csvCell(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// CSV text -> rows of cells. Handles quotes ("a, b" and "" inside quotes),
// Windows and Mac line endings, and Excel's byte-order mark.
export function parseCsv(text) {
  const src = String(text || "").replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
}

const tidy = text => String(text ?? "").trim().replace(/\s+/g, " ");

// "a; b;;A" -> ["a", "b"]: trimmed, blanks dropped, each once (any capitals).
export function splitList(cell) {
  const seen = new Set();
  const out = [];
  for (const part of String(cell ?? "").split(";")) {
    const item = tidy(part);
    if (!item || seen.has(item.toLowerCase())) continue;
    seen.add(item.toLowerCase());
    out.push(item);
  }
  return out;
}

// "s12ab" -> "S1 2AB", "s1" -> "S1"; anything else -> null.
export function cleanPostcode(text) {
  const compact = String(text ?? "").toUpperCase().replace(/\s+/g, "");
  if (FULL_POSTCODE_RE.test(compact) && compact.length >= 5) return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
  return OUTCODE_RE.test(compact) ? compact : null;
}

// "3x1hour" -> { samples: 3, spacing: "hour" }; blank -> the defaults.
// Returns { samples, spacing } or { error }.
export function parseSamplesCode(cell) {
  const text = String(cell ?? "").toLowerCase().replace(/\s+/g, "");
  if (!text) return { samples: DEFAULT_SAMPLES, spacing: DEFAULT_SPACING };
  const help = `Samples: use a code like 3x1hour (${SAMPLE_COUNTS.join(", ")} × 1min, 15min, 1hour or 1day)`;
  const m = text.match(/^(\d+)[x×*](.+)$/);
  if (!m) return { error: help };
  const samples = parseInt(m[1], 10);
  const spacing = Object.keys(SPACING_ALIASES).find(id => SPACING_ALIASES[id].includes(m[2]));
  if (!SAMPLE_COUNTS.includes(samples) || !spacing) return { error: help };
  return { samples, spacing };
}

// { samples: 3, spacing: "hour" } -> "3x1hour"
export function samplesCode(samples, spacing) {
  return `${samples}x${SPACING_CODES[spacing]}`;
}

// "every hour", "every 15 min" ...
export function spacingText(spacing) {
  return { minute: "every minute", "15min": "every 15 min", hour: "every hour", day: "every day" }[spacing] || "";
}

// "10", "10 miles", "10mi" -> 10; blank, "0", "none" -> null (no radius).
// Returns { radius } or { error }.
export function parseRadius(cell) {
  const text = String(cell ?? "").toLowerCase().replace(/\s+/g, "").replace(/(miles?|mi)$/, "");
  if (!text || text === "0" || text === "none") return { radius: null };
  const miles = Number(text);
  if (!RADIUS_MILES.includes(miles)) return { error: `Radius must be blank or ${RADIUS_MILES.join(", ")} (miles)` };
  return { radius: miles };
}

// The file's text -> { lines, error }. Each line:
//   { lineNumber, website, domain, businessName, locations, radius, extras,
//     keywords, samples, spacing, tags, errors, notes, sameWebsiteAs }
// lineNumber counts scans from 1 (the column names aren't counted).
// errors block that line; notes and sameWebsiteAs only flag it.
// The least a line needs: one postcode (location or extra_postcodes) and one
// keyword. No website = no business tracked and no tags; no location = no radius.
export function readCsv(text) {
  const rows = parseCsv(text);
  const filled = rows.map((cells, i) => ({ cells, lineNumber: i + 1 }))
    .filter(r => r.cells.some(c => tidy(c) !== ""));
  if (filled.length === 0) return { lines: [], error: "The file is empty." };

  const header = filled[0].cells.map(c => tidy(c).toLowerCase());
  const missing = REQUIRED_COLUMNS.filter(c => !header.includes(c));
  if (missing.length > 0) {
    return { lines: [], error: `The first line must be the column names (${CSV_COLUMNS.join(", ")}). Missing: ${missing.join(", ")}. Download the template to see the layout.` };
  }
  const data = filled.slice(1);
  if (data.length === 0) return { lines: [], error: "The file has the column names but no snapshots." };
  if (data.length > MAX_CSV_LINES) {
    return { lines: [], error: `Up to ${MAX_CSV_LINES} snapshots per upload - this file has ${data.length}.` };
  }

  const lines = data.map(({ cells }, index) => {
    const get = name => {
      const i = header.indexOf(name);
      return i >= 0 ? cells[i] ?? "" : "";
    };
    return readLine(get, index + 1);
  });

  // The same website on several lines: separate scans, flagged.
  for (const line of lines) {
    line.sameWebsiteAs = line.domain
      ? lines.filter(other => other !== line && other.domain === line.domain).map(other => other.lineNumber)
      : [];
  }
  return { lines, error: null };
}

function readLine(get, lineNumber) {
  const errors = [];
  const notes = [];

  const website = tidy(get("website"));
  const domain = domainOf(website);
  if (website && !domain) errors.push(`"${website}" isn't a website`);

  const postcodeList = (cell, label) => {
    const out = [];
    for (const item of splitList(cell)) {
      const code = cleanPostcode(item);
      if (!code) errors.push(`${label}: "${item}" isn't a UK postcode or district`);
      else if (!out.includes(code)) out.push(code);
    }
    return out;
  };
  const errorsBefore = errors.length;
  const locations = postcodeList(get("location"), "Location");
  const extras = postcodeList(get("extra_postcodes"), "Extra postcodes");
  if (locations.length === 0 && extras.length === 0 && errors.length === errorsBefore) {
    errors.push("Add at least one postcode (location or extra_postcodes)");
  }

  const radius = parseRadius(get("radius"));
  if (radius.error) errors.push(radius.error);
  else if (radius.radius && locations.length === 0) notes.push("Radius ignored: no location to centre it on");

  const keywords = splitList(get("keywords")).map(k => k.slice(0, MAX_KEYWORD_LENGTH));
  if (keywords.length === 0) errors.push("Add at least one keyword");
  if (keywords.length > MAX_KEYWORDS) errors.push(`Up to ${MAX_KEYWORDS} keywords - this line has ${keywords.length}`);

  const samples = parseSamplesCode(get("samples"));
  if (samples.error) errors.push(samples.error);

  const tags = splitList(get("tags"));
  if (tags.length > MAX_TAGS) errors.push(`Up to ${MAX_TAGS} tags - this line has ${tags.length}`);
  const longTag = tags.find(t => t.length > MAX_TAG_LENGTH);
  if (longTag) errors.push(`Tags can be up to ${MAX_TAG_LENGTH} characters ("${longTag.slice(0, 20)}…")`);

  if (!website) {
    notes.push(
      "No website: no business will be tracked - pick yours in the snapshot once it is running" +
      (tags.length > 0 ? ". Tags won't be added" : "")
    );
  }

  return {
    lineNumber,
    website,
    domain,
    // Without a website the scan is saved with no business at all.
    businessName: website ? tidy(get("business_name")) : "",
    locations,
    radius: radius.error ? null : radius.radius,
    extras,
    keywords,
    samples: samples.error ? DEFAULT_SAMPLES : samples.samples,
    spacing: samples.error ? DEFAULT_SPACING : samples.spacing,
    tags,
    errors,
    notes,
    sameWebsiteAs: [],
  };
}

// Every postcode the lines need looked up (locations and extras), each once.
export function postcodesToLookUp(lines) {
  return [...new Set(lines.flatMap(l => [...l.locations, ...l.extras]))];
}

// A district from postcode_districts as a place: { postcode, lat, lng, places }.
// Districts on the list don't need looking up; full postcodes and districts
// not on it (e.g. BT) do.
export function placesFromDistricts(codes, districts) {
  const byOutcode = new Map((districts || []).map(d => [d.outcode, d]));
  const found = {};
  const toFetch = [];
  for (const code of codes) {
    const d = OUTCODE_RE.test(code) ? byOutcode.get(code) : null;
    if (d) found[code] = { postcode: d.outcode, lat: d.lat, lng: d.lng, places: d.area ? [d.area] : [] };
    else toFetch.push(code);
  }
  return { found, toFetch };
}

// One line's scan: the plan (as on the search page), the request for the
// Worker, and any more errors (postcodes not found, over the account's limit).
// places: { "S1 2AB": { postcode, lat, lng, places } } - a postcode missing
// from it wasn't found.
export function planCsvLine(line, { places, districts, niches, maxPostcodes, now }) {
  const errors = [...line.errors];
  const notFound = [...line.locations, ...line.extras].filter(code => !places[code]);
  if (notFound.length > 0) errors.push(`Not found: ${notFound.join(", ")}`);

  const centres = line.locations.filter(code => places[code]).map((code, i) => ({ ...places[code], isMain: i === 0 }));
  const extras = line.extras.filter(code => places[code]).map(code => places[code]);
  const plan = planScan({
    keywords: line.keywords, samples: line.samples, spacing: line.spacing,
    centres, radius: line.radius, extras, districts, maxPostcodes, niches, now,
  });
  if (plan.overLimit) errors.push(`${plan.postcodes.length} postcodes - your account can search up to ${maxPostcodes} per search`);
  // A location whose own district isn't on the list gets no radius picks.
  const noPicks = line.radius
    ? centres.filter(c => !(districts || []).some(d => d.outcode === outcodeOf(c.postcode))).map(c => c.postcode)
    : [];

  const main = centres[0] || null;
  const request = buildScanRequest({
    name: plan.name,
    keywords: plan.tidied,
    postcodes: plan.postcodes,
    niche: plan.runNiche,
    subGroup: plan.subGroup,
    suggestionId: null,
    business: {
      name: line.domain ? line.businessName : "", website: line.domain ? line.website : "",
      postcode: main ? main.postcode : "", lat: main ? main.lat : null, lng: main ? main.lng : null,
    },
    samples: line.samples,
    spacing: line.spacing,
  });

  return { line, plan, request, errors, noPicks, ready: errors.length === 0 && plan.canRun };
}

// Totals for the preview: lines ready to run and the searches they make.
export function csvTotals(planned) {
  const ready = planned.filter(p => p.ready);
  return {
    lines: planned.length,
    ready: ready.length,
    blocked: planned.length - ready.length,
    searches: ready.reduce((sum, p) => sum + p.plan.total, 0),
    flagged: planned.filter(p => p.line.sameWebsiteAs.length > 0).length,
  };
}
